import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, CalendarClock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { plusDays, nextWeekday, fmtDue, subjectStyle } from "@/lib/subjects";
import { PhotoHomework } from "@/components/PhotoHomework";
import { useEdition } from "@/lib/edition";

export const useSubjects = () => {
  const [subs, setSubs] = useState([]);
  useEffect(() => { api.get("/homework/subjects").then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => setSubs([])); }, []);
  return subs;
};

const Chip = ({ id, on, onClick, children, teal }) => (
  <button type="button" data-testid={id} onClick={onClick}
    className={`h-9 px-3 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition-colors ${on ? "bg-brand text-white border-brand shadow-sm shadow-brand/30" : teal ? "bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100" : "bg-surface text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
    {children}
  </button>
);

export const QuickAddHomework = ({ onAdded, compact = false, defaultKind = "homework" }) => {
  const subs = useSubjects();
  const { w } = useEdition();
  const [kind, setKind] = useState(defaultKind);
  useEffect(() => { setKind(defaultKind); }, [defaultKind]);
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [due, setDue] = useState(plusDays(1));
  const [minutes, setMinutes] = useState("");
  const [busy, setBusy] = useState(false);
  const nextLesson = subs.find((s) => s.subject === subject)?.next_lesson;
  useEffect(() => { if (nextLesson) setDue(nextLesson); }, [nextLesson]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post("/homework", { title: title.trim(), subject, due, kind, ...(minutes && kind !== "test" ? { minutes: Number(minutes) } : {}) });
      toast.success(`${kind === "test" ? w("Test added", "Deadline added") : "Added"} · ${kind === "test" ? "on" : "due"} ${fmtDue(due)}`);
      setTitle(""); setSubject(""); setDue(plusDays(1)); setMinutes("");
      onAdded?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <form data-testid="quick-add-homework" onSubmit={submit} className="card p-4 sm:p-5 focus-within:ring-2 focus-within:ring-gold-400/50 transition-shadow">
      <div className="flex items-center justify-between gap-2">
        {!compact && <p className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-teal-700">{kind === "test" ? w("Add a test", "Add a deadline") : w("Add homework", "Add a task")}</p>}
        <div data-testid="quick-add-kind" className="inline-flex rounded-full bg-slate-100 p-0.5">
          {[["homework", w("Homework", "Task")], ["test", w("Test", "Deadline")]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`quick-add-kind-${k}`} onClick={() => setKind(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${kind === k ? "bg-brand text-white" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <input data-testid="quick-add-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200}
          placeholder={kind === "test" ? w("e.g. Chemistry end-of-unit test", "e.g. Q3 report to finance") : w("e.g. Maths worksheet p.42", "e.g. Reply to supplier quote")} className="flex-1 min-w-0 bg-transparent py-2 text-base sm:text-lg font-medium text-slate-900 placeholder:text-slate-400 outline-none" />
        <PhotoHomework onAdded={onAdded} />
        <button data-testid="quick-add-submit" disabled={busy || !title.trim()} aria-label={kind === "test" ? "Add test" : "Add homework"}
          className="grid place-items-center w-11 h-11 shrink-0 rounded-2xl bg-brand text-white hover:bg-brand-dark active:scale-95 disabled:opacity-40 transition-[background-color,transform]">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
        </button>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
        <select data-testid="quick-add-subject" value={subject} onChange={(e) => setSubject(e.target.value)}
          className={`h-9 max-w-[11rem] rounded-full border px-3 text-xs font-semibold outline-none ${subject ? subjectStyle(subject).chip : "bg-slate-100 text-slate-700 border-slate-200"}`}>
          <option value="">{w("Subject…", "Project…")}</option>
          {subs.map((s) => <option key={s.subject} value={s.subject}>{s.subject}</option>)}
        </select>
        {nextLesson && <Chip id="due-chip-next-lesson" teal on={due === nextLesson} onClick={() => setDue(nextLesson)}><CalendarClock className="w-3.5 h-3.5" />{w("Next lesson", "Next meeting")} · {fmtDue(nextLesson)}</Chip>}
        <Chip id="due-chip-tomorrow" on={due === plusDays(1) && due !== nextLesson} onClick={() => setDue(plusDays(1))}>Tomorrow</Chip>
        <Chip id="due-chip-friday" on={due === nextWeekday(5) && due !== plusDays(1) && due !== nextLesson} onClick={() => setDue(nextWeekday(5))}>Friday</Chip>
        <Chip id="due-chip-week" on={due === plusDays(7) && due !== nextLesson} onClick={() => setDue(plusDays(7))}>In a week</Chip>
        <input data-testid="quick-add-due" type="date" value={due} min={plusDays(0)} onChange={(e) => e.target.value && setDue(e.target.value)}
          className="h-9 rounded-full border border-slate-200 bg-surface px-3 text-xs font-mono text-slate-700 outline-none" />
        {kind !== "test" && (
          <select data-testid="quick-add-minutes" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="h-9 rounded-full border border-slate-200 bg-surface px-3 text-xs font-semibold text-slate-700 outline-none">
            <option value="">How long?</option>
            {[10, 15, 20, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
          </select>
        )}
      </div>
    </form>
  );
};
